import type {
  LedgerImportAction,
  LedgerImportDTO,
  LedgerImportRowDTO,
  LedgerImportRowStatus,
} from "../data/ledgerImportApi";

// How the four outcomes of the diff are named and coloured on screen.

/**
 * Problems first, then work, then the rows that cost nothing.
 *
 * `unchanged` is last but never hidden: "125 unchanged" is the evidence that
 * the other 125 lines were looked at rather than left out of the file, and a CA
 * checking that their edit to row 61 registered needs somewhere to look.
 */
export const ACTION_ORDER: LedgerImportAction[] = ["invalid", "new", "update", "unchanged"];

export const ACTION_LABEL: Record<LedgerImportAction, string> = {
  invalid: "Needs fixing",
  new: "To be added",
  update: "To be changed",
  unchanged: "Unchanged",
};

/**
 * The same groups once the push has run — outcome tense, not intent tense.
 * "To be added: 8" over eight rows all badged "Added" reads as a queue that
 * never drained. The rows stay grouped by sheet action (see the rows
 * component's docblock for why); only the heading changes tense.
 */
export const PUSHED_ACTION_LABEL: Record<LedgerImportAction, string> = {
  invalid: "Not sent — needs fixing",
  new: "Added",
  update: "Changed",
  unchanged: "Unchanged — not sent",
};

export const ACTION_STYLE: Record<LedgerImportAction, string> = {
  invalid: "bg-amber-500/10 text-amber-700 dark:text-amber-400",
  new: "bg-emerald-500/10 text-emerald-700 dark:text-emerald-400",
  update: "bg-sky-500/10 text-sky-700 dark:text-sky-400",
  unchanged: "bg-muted text-muted-foreground",
};

/** The column headings a diff can name, in the order the sheet has them. */
const FIELD_LABEL: Record<string, string> = {
  account_name: "Name",
  account_type: "Type",
  parent: "Parent",
  description: "Description",
};

export function fieldLabel(field: string): string {
  return FIELD_LABEL[field] ?? field;
}

/** An empty cell reads better as a dash than as nothing at all in a before/after. */
export function cellText(value: string | null): string {
  return value === null || value === "" ? "—" : value;
}

export function groupByAction(
  rows: LedgerImportRowDTO[]
): { action: LedgerImportAction; rows: LedgerImportRowDTO[] }[] {
  return ACTION_ORDER.map((action) => ({
    action,
    rows: rows.filter((row) => row.action === action),
  })).filter((group) => group.rows.length > 0);
}

/**
 * Warnings on rows that are otherwise fine.
 *
 * Counted separately from `invalid_rows` because they are a different question.
 * An invalid row will not be sent; a warned row will be, and the warning is the
 * last chance to say don't.
 */
export function warningCount(rows: LedgerImportRowDTO[]): number {
  return rows.filter(
    (row) => row.action !== "invalid" && row.issues.some((issue) => issue.severity === "warning")
  ).length;
}

// ── After the push ──────────────────────────────────────────────────────────

/**
 * What happened to a row, in the past tense.
 *
 * Only shown once a push has run. Before that every row is `pending`, and
 * labelling a hundred rows "Not sent" would read as a problem rather than as
 * "nothing has happened yet".
 */
export const ROW_STATUS_LABEL: Record<LedgerImportRowStatus, string> = {
  pending: "Not sent",
  created: "Added",
  updated: "Changed",
  skipped: "Nothing to do",
  failed: "Not added",
};

export const ROW_STATUS_STYLE: Record<LedgerImportRowStatus, string> = {
  pending: "bg-muted text-muted-foreground",
  created: "bg-emerald-500/10 text-emerald-700 dark:text-emerald-400",
  updated: "bg-sky-500/10 text-sky-700 dark:text-sky-400",
  skipped: "bg-muted text-muted-foreground",
  failed: "bg-amber-500/10 text-amber-700 dark:text-amber-400",
};

/**
 * Has this import been sent anywhere yet?
 *
 * The question the screen turns on: before, it shows what WOULD change and
 * offers a push; after, it shows what DID and offers no such thing. `status`
 * rather than a count, because a push that failed every row has still been run
 * and must not offer itself again as though it had not.
 */
export function hasBeenPushed(summary: LedgerImportDTO): boolean {
  return summary.status === "committing" || summary.status === "done";
}

/**
 * Rows the push meant to send that did not go through: refused by Zoho
 * (`failed`), or held back because their parent never arrived (`skipped`).
 * Either way the ledger is not in the client's books and the fix is in the
 * sheet. New and changed rows only: unchanged rows are settled as `skipped`
 * too, because there was nothing to send.
 */
export function notAddedRows(rows: LedgerImportRowDTO[]): LedgerImportRowDTO[] {
  return rows.filter(
    (row) =>
      (row.action === "new" || row.action === "update") &&
      (row.status === "failed" || row.status === "skipped")
  );
}

/**
 * What the push did, in words. The push only ever adds (`new`) or changes
 * (`update`), so the verb follows whichever of those the import had.
 */
export function pushVerb(summary: LedgerImportDTO): {
  done: string;
  notDone: (count: number) => string;
} {
  const be = (count: number) => (count === 1 ? "was" : "were");
  if (summary.changed_rows === 0) return { done: "added to Zoho", notDone: (n) => `${be(n)} not added` };
  if (summary.new_rows === 0) return { done: "changed in Zoho", notDone: (n) => `${be(n)} not changed` };
  return { done: "added or changed in Zoho", notDone: () => "did not go through" };
}

/** `Zoho Books error (HTTP 400, code 11039) — <message>`, as ZohoBooksClient builds it. */
const ZOHO_ERROR = /^Zoho Books error \(HTTP \d+, code (-?\d+)\)\s*[—–-]\s*([\s\S]*)$/;

/** Zoho's "Creation of sub account is not supported for this account." */
const ZOHO_NO_SUB_ACCOUNT = 11039;

export interface RowRejection {
  /** The line to lead with: what went wrong and, where we know it, what to do. */
  reason: string;
  /** Zoho's own sentence, shown small underneath, when `reason` is ours rather than Zoho's. */
  zohoMessage: string | null;
  /** Zoho's error code, when the refusal came from Zoho. */
  code: number | null;
}

/**
 * Turn a row's stored error into something an accountant can act on.
 *
 * Zoho's sentence is usually good enough to lead with once the HTTP prefix is
 * gone ("The account name already exists."). 11039 is the exception: it says
 * "this account" without naming it, and the fix is always the same, so we name
 * the parent and say what to change. Errors that are not Zoho's (our own
 * "Something went wrong…", or a parent that never arrived) are shown as they are.
 */
export function explainRejection(error: string | null, parentName: string | null): RowRejection {
  if (error === null || error.trim() === "") {
    return { reason: "No reason was recorded for this row.", zohoMessage: null, code: null };
  }

  const match = ZOHO_ERROR.exec(error.trim());
  if (match === null) {
    return { reason: error, zohoMessage: null, code: null };
  }

  const code = Number(match[1]);
  const message = match[2].trim() || "Zoho gave no message.";

  if (code === ZOHO_NO_SUB_ACCOUNT && parentName !== null) {
    return {
      reason: `Zoho doesn't allow new ledgers under ${parentName}. Clear the Parent Ledger cell for this row and upload again.`,
      zohoMessage: message,
      code,
    };
  }

  return { reason: message, zohoMessage: null, code };
}
